'use client';

import { Progress as ProgressPrimitive } from '@base-ui/react/progress';

import { type StatusTone, statusFillToneVariants } from '@playbooks/ui/components/component-recipes';
import { cn } from '@playbooks/ui/lib/utils';

type ProgressProps = ProgressPrimitive.Root.Props & { tone?: StatusTone };

function Progress({ className, children, value, tone = 'action', ...props }: ProgressProps) {
	// Theme
	const rootClassName = cn('flex flex-wrap gap-3', className);

	// Render
	return (
		<ProgressPrimitive.Root value={value} data-slot='progress' data-tone={tone} className={rootClassName} {...props}>
			{children}
			<ProgressTrack>
				<ProgressIndicator tone={tone} />
			</ProgressTrack>
		</ProgressPrimitive.Root>
	);
}

function ProgressTrack({ className, ...props }: ProgressPrimitive.Track.Props) {
	// Theme
	const rootClassName = cn(
		'relative flex h-1 w-full items-center overflow-x-hidden rounded-full bg-surface-hover',
		className,
	);

	// Render
	return <ProgressPrimitive.Track className={rootClassName} data-slot='progress-track' {...props} />;
}

type ProgressIndicatorProps = ProgressPrimitive.Indicator.Props & { tone?: StatusTone };

function ProgressIndicator({ className, tone = 'action', ...props }: ProgressIndicatorProps) {
	// Theme
	const rootClassName = cn('h-full transition-all', statusFillToneVariants({ tone }), className);

	// Render
	return <ProgressPrimitive.Indicator data-slot='progress-indicator' className={rootClassName} {...props} />;
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
	// Theme
	const rootClassName = cn('text-sm font-medium', className);

	// Render
	return <ProgressPrimitive.Label className={rootClassName} data-slot='progress-label' {...props} />;
}

function ProgressValue({ className, ...props }: ProgressPrimitive.Value.Props) {
	// Theme
	const rootClassName = cn('ml-auto text-sm text-foreground-body tabular-nums', className);

	// Render
	return <ProgressPrimitive.Value className={rootClassName} data-slot='progress-value' {...props} />;
}

export { Progress, ProgressTrack, ProgressIndicator, ProgressLabel, ProgressValue };
export type { ProgressIndicatorProps, ProgressProps };
